import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useBrand } from "../lib/brand";
import { isClientView } from "../lib/view-mode";
import { Button } from "../components/ui/button";
import { LogOut, LayoutDashboard, Sparkles } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";

export default function NavBar() {
  const { user, logout } = useAuth();
  const { tenant } = useBrand();
  const nav = useNavigate();
  const loc = useLocation();
  const onLanding = loc.pathname === "/";
  const clientView = isClientView(loc.pathname, tenant);

  const scrollToSection = (id) => (e) => {
    e.preventDefault();
    if (!onLanding) {
      nav("/", { state: { scrollTo: id } });
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Branded "powered by" wordmark for client views
  const Brand = () => (
    <Link to={tenant ? "/" : "/"} data-testid="nav-logo" className="flex items-center gap-2">
      {tenant?.brand_logo_url ? (
        <img src={tenant.brand_logo_url} alt={tenant.company || tenant.name || "Brand"}
             className="h-9 w-auto max-w-[180px] object-contain" />
      ) : (
        <>
          <div className="w-8 h-8 rounded-sm bg-accent flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-accent-foreground" />
          </div>
          <div className="leading-tight">
            <div className="font-sans-ui font-bold tracking-tight text-lg">
              {tenant?.company || <>BizPulse <span className="text-accent">AI</span></>}
            </div>
            <div className="text-[10px] tracking-widest uppercase text-muted-foreground -mt-0.5">
              {tenant ? "Powered by BizPulse AI" : "Executive Diagnostics"}
            </div>
          </div>
        </>
      )}
    </Link>
  );

  if (clientView) {
    // Minimal nav for a consultant's prospect — no marketing/product links.
    return (
      <header className="sticky top-0 z-40 glass-nav">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Brand />
          {tenant ? (
            <div className="text-xs text-muted-foreground hidden md:block">
              {tenant.name}{tenant.company ? ` · ${tenant.company}` : ""}
            </div>
          ) : null}
        </div>
      </header>
    );
  }

  return (
    <header className={`sticky top-0 z-40 ${onLanding ? "glass-nav" : "bg-background border-b border-border/60"}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <Brand />
        <nav className="hidden md:flex items-center gap-7 text-sm">
          <a href="/#features" onClick={scrollToSection("features")} className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer" data-testid="nav-features">Features</a>
          <a href="/#pricing" onClick={scrollToSection("pricing")} className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer" data-testid="nav-pricing">Pricing</a>
          <a href="/#contact" onClick={scrollToSection("contact")} className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer" data-testid="nav-contact">Contact</a>
          <Link to="/sample" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-sample">Sample Discovery</Link>
        </nav>
        <div className="flex items-center gap-2">
          <LanguageSwitcher compact />
          {user ? (
            <>
              <Button variant="ghost" size="sm" data-testid="nav-dashboard-btn" onClick={() => nav(user.role === "admin" ? "/admin" : "/dashboard")}>
                <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
              </Button>
              <Button variant="outline" size="sm" data-testid="nav-logout-btn" onClick={() => { logout(); nav("/"); }}>
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" data-testid="nav-login-btn" onClick={() => nav("/login")}>Sign in</Button>
              <Button size="sm" className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="nav-register-btn" onClick={() => nav("/register")}>
                Start Free
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
